import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Menu, Phone, Search, ShoppingCart, Tag, X } from "lucide-react";
import { useSite } from "../../lib/site";
import { useCart } from "../../lib/cart";
import { useCheckoutEnabled } from "../../lib/checkout";
import { pageHref } from "../../lib/format";

/**
 * All top-level sections stay visible - the owner does not want any of them
 * tucked away behind a "More" menu (Surplus Stock especially). Spacing is
 * tightened instead so the full set fits the bar.
 */
const NAV_VISIBLE = 99;

function MegaPanel({ node, onNavigate }) {
  const kids = node.children || [];
  // split children into columns of ~6
  const cols = [];
  const per = Math.ceil(kids.length / Math.min(3, Math.max(1, Math.ceil(kids.length / 7)))) || 1;
  for (let i = 0; i < kids.length; i += per) cols.push(kids.slice(i, i + per));

  // Width follows the column count. The "not sure what fits" prompt used to be a
  // fat 208px column on the right, which on a 3-item menu left the links about
  // 60px and wrapped every label down five lines. It is now a slim strip along
  // the bottom, so the links get the full width of the panel.
  const minWidth = Math.min(3, cols.length) * 250 + 40;

  return (
    <div
      data-testid="site-nav-panel"
      className="absolute left-0 top-full z-50 pt-3"
    >
      <div
        className="rounded-xl border p-4 shadow-[0_30px_80px_rgba(0,0,0,0.65)] backdrop-blur"
        style={{
          background: "rgba(17,21,19,0.98)",
          borderColor: "rgba(198,162,74,0.22)",
          minWidth,
          maxWidth: "min(90vw, 900px)",
        }}
      >
        <div className="grid gap-x-6 gap-y-1" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
          {cols.map((col, ci) => (
            <div key={ci} className="space-y-0.5">
              {col.map((c) => (
                <div key={c.slug || c.label}>
                  <Link
                    to={pageHref(c.slug)}
                    onClick={onNavigate}
                    className="block rounded-md px-2.5 py-1.5 text-sm font-medium leading-snug text-[color:var(--nq-sand)] transition hover:bg-white/5 hover:text-[color:var(--nq-gold)]"
                  >
                    {c.label}
                  </Link>
                  {(c.children || []).length > 0 && (
                    <div className="mb-1 ml-2 border-l pl-3" style={{ borderColor: "rgba(198,162,74,0.18)" }}>
                      {c.children.map((g) => (
                        <Link
                          key={g.slug || g.label}
                          to={pageHref(g.slug)}
                          onClick={onNavigate}
                          className="block rounded-md px-2 py-1 text-[13px] leading-snug text-[color:var(--nq-stone)] transition hover:text-[color:var(--nq-gold)]"
                        >
                          {g.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div
          className="mt-3 flex items-center justify-between gap-4 border-t pt-3"
          style={{ borderColor: "rgba(198,162,74,0.16)" }}
        >
          <p className="whitespace-nowrap text-xs text-[color:var(--nq-stone)]">
            Not sure what fits?
          </p>
          <Link
            to={`/enquire?refType=page&refId=${node.slug || ""}&refTitle=${encodeURIComponent(node.label)}`}
            onClick={onNavigate}
            className="btn-gold shrink-0 whitespace-nowrap px-3.5 py-1.5 text-xs"
            data-testid="nav-panel-enquire-cta"
          >
            Enquire now
          </Link>
        </div>
      </div>
    </div>
  );
}

/* Flatten the 3-level menu so the mobile drawer needs no recursive component
   (this Babel version overflows its stack on self-referencing components). */
function flattenNav(nodes, parentKey = "", depth = 0, out = []) {
  nodes.forEach((n, i) => {
    const key = `${parentKey}${parentKey ? "-" : ""}${i}`;
    out.push({ node: n, key, parentKey, depth, hasKids: (n.children || []).length > 0 });
    flattenNav(n.children || [], key, depth + 1, out);
  });
  return out;
}

function MobileMenuTree({ nav, onNavigate }) {
  const [open, setOpen] = useState({});
  const rows = flattenNav(nav.filter((n) => n.label && n.label.trim()));

  const visible = rows.filter((r) => {
    if (!r.parentKey) return true;
    // every ancestor must be expanded
    const parts = r.parentKey.split("-");
    for (let i = 1; i <= parts.length; i += 1) {
      if (!open[parts.slice(0, i).join("-")]) return false;
    }
    return true;
  });

  return (
    <div className="p-2" data-testid="mobile-menu-accordion">
      {visible.map((r) => (
        <div
          key={r.key}
          className="flex items-center"
          style={{ paddingLeft: `${r.depth * 14}px` }}
        >
          <Link
            to={pageHref(r.node.slug)}
            onClick={onNavigate}
            className={
              r.node.highlight
                ? "mb-1 flex flex-1 items-center gap-2 rounded-md px-3 py-3 text-sm font-bold uppercase tracking-wide"
                : "flex-1 rounded-md px-3 py-2.5 text-sm text-[color:var(--nq-sand)] hover:bg-white/5"
            }
            style={
              r.node.highlight
                ? { background: "var(--nq-gold)", color: "var(--nq-black)" }
                : undefined
            }
            data-testid="mobile-menu-link"
          >
            {r.node.highlight && <Tag className="h-4 w-4 shrink-0" />}
            {r.node.label}
          </Link>
          {r.hasKids && (
            <button
              type="button"
              aria-label={`Toggle ${r.node.label}`}
              onClick={() => setOpen((o) => ({ ...o, [r.key]: !o[r.key] }))}
              className="rounded-md p-2 text-[color:var(--nq-gold)] hover:bg-white/5"
              data-testid="mobile-menu-toggle"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${open[r.key] ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Header() {
  const { site, nav } = useSite();
  const { count } = useCart();
  const payments = useCheckoutEnabled();
  const [openSlug, setOpenSlug] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenSlug(null);
    setSearchOpen(false);
  }, [location.pathname]);

  // Stop the page behind the drawer scrolling under your thumb.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileOpen]);

  const phone = site?.contact?.phone || "(07) 4774 8859";
  // Shipped with the frontend build, not read from the DB: the logo must be the same
  // on preview and production regardless of what each database has stored.
  const logo = "/nq-springs-logo-v3.png";

  return (
    <header
      className="sticky top-0 z-50 border-b backdrop-blur"
      style={{ background: "rgba(11,13,12,0.93)", borderColor: "rgba(255,255,255,0.08)" }}
      data-testid="site-header"
    >
      {/* utility bar */}
      <div className="hidden border-b md:block" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="container-nq flex h-9 items-center justify-between text-xs">
          <p className="text-[color:var(--nq-stone)]">
            {site?.branding?.tagline || "Turning rough into smooth since 1961"}
          </p>
          <div className="flex items-center gap-5 text-[color:var(--nq-stone)]">
            <span>{site?.contact?.address || "321 Ingham Rd, Garbutt QLD 4814"}</span>
            <a
              href={`tel:${phone.replace(/[^\d+]/g, "")}`}
              className="flex items-center gap-1.5 font-semibold gold-text"
              data-testid="header-phone-link"
            >
              <Phone className="h-3.5 w-3.5" />
              {phone}
            </a>
          </div>
        </div>
      </div>

      <div className="container-nq flex h-16 items-center gap-4 lg:h-20">
        <Link to="/" className="flex shrink-0 items-center gap-3" data-testid="site-logo-link">
          {logo ? (
            <img src={logo} alt="NQ Springs" className="h-9 w-auto lg:h-12" />
          ) : (
            <span className="text-xl font-bold tracking-tight">
              NQ<span className="gold-text">SPRINGS</span>
            </span>
          )}
        </Link>


        <div className="ml-auto flex items-center gap-1.5 xl:ml-3">
          <button
            type="button"
            aria-label="Search"
            onClick={() => setSearchOpen((s) => !s)}
            className="rounded-lg p-2.5 text-[color:var(--nq-sand)] transition hover:bg-white/5 hover:text-[color:var(--nq-gold)]"
            data-testid="header-search-button"
          >
            <Search className="h-5 w-5" />
          </button>

          {payments && (
            <Link
              to="/cart"
              className="relative rounded-lg p-2.5 text-[color:var(--nq-sand)] transition hover:bg-white/5 hover:text-[color:var(--nq-gold)]"
              data-testid="header-cart-link"
              aria-label="Cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span
                  className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold"
                  style={{ background: "var(--nq-gold)", color: "var(--nq-black)" }}
                  data-testid="header-cart-count"
                >
                  {count}
                </span>
              )}
            </Link>
          )}

          <Link to="/enquire" className="btn-gold hidden whitespace-nowrap lg:inline-flex" data-testid="header-enquire-button">
            Enquire now
          </Link>

          <Link
            to="/new-vehicle-build"
            className="btn-outline-nq hidden whitespace-nowrap lg:inline-flex"
            data-testid="header-new-build-button"
          >
            New build
          </Link>

          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2.5 text-[color:var(--nq-sand)] transition hover:bg-white/5 xl:hidden"
            data-testid="mobile-menu-open-button"
            id="mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Full-width nav row. The owner wants every top-level section visible
          (Surplus Stock especially), and 13 items will not fit beside the logo
          on a 1280-1536px laptop - so the nav gets its own row. */}
      <div
        className="hidden border-t xl:block"
        style={{ borderColor: "rgba(255,255,255,0.06)" }}
      >
        <div className="container-nq">
        {/* desktop nav */}
        <nav
          className="flex flex-wrap items-center justify-center gap-y-1 py-1.5"
          data-testid="site-nav"
          onMouseLeave={() => setOpenSlug(null)}
        >
          {nav
            .filter((n) => n.label && n.label.trim())
            .slice(0, NAV_VISIBLE)
            .map((node) => {
              const kids = node.children || [];
              const active = openSlug === (node.slug || node.label);
              return (
                <div
                  key={node.slug || node.label}
                  className="relative"
                  onMouseEnter={() => setOpenSlug(kids.length ? node.slug || node.label : null)}
                  data-testid="site-nav-item"
                >
                  <NavLink
                    to={pageHref(node.slug)}
                    className={({ isActive }) =>
                      node.highlight
                        ? `flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-bold uppercase tracking-wide transition ${
                            isActive ? "opacity-90" : "hover:brightness-110"
                          }`
                        : `flex items-center gap-1 whitespace-nowrap px-1.5 py-2 text-[12px] font-semibold transition-colors ${
                            isActive || active
                              ? "text-[color:var(--nq-gold)]"
                              : "text-[color:var(--nq-sand)] hover:text-[color:var(--nq-gold)]"
                          }`
                    }
                    style={
                      node.highlight
                        ? { background: "var(--nq-gold)", color: "var(--nq-black)" }
                        : undefined
                    }
                    onClick={() => setOpenSlug(null)}
                    data-testid={node.highlight ? "site-nav-highlight" : undefined}
                  >
                    {node.highlight && <Tag className="h-3.5 w-3.5" />}
                    {node.label}
                    {kids.length > 0 && <ChevronDown className="h-3 w-3 opacity-70" />}
                  </NavLink>
                  {kids.length > 0 && active && (
                    <MegaPanel node={node} onNavigate={() => setOpenSlug(null)} />
                  )}
                </div>
              );
            })}

          {/* surplus top-level sections collapse into a More menu so the bar
              never overflows on narrower desktops */}
          {nav.filter((n) => n.label && n.label.trim()).length > NAV_VISIBLE && (
            <div
              className="relative"
              onMouseEnter={() => setOpenSlug("__more__")}
              data-testid="site-nav-item"
            >
              <button
                type="button"
                className={`flex items-center gap-1 whitespace-nowrap px-1.5 py-2 text-[12px] font-semibold transition-colors ${
                  openSlug === "__more__"
                    ? "text-[color:var(--nq-gold)]"
                    : "text-[color:var(--nq-sand)] hover:text-[color:var(--nq-gold)]"
                }`}
                data-testid="site-nav-more"
              >
                More <ChevronDown className="h-3 w-3 opacity-70" />
              </button>
              {openSlug === "__more__" && (
                <MegaPanel
                  node={{
                    label: "More",
                    children: nav.filter((n) => n.label && n.label.trim()).slice(NAV_VISIBLE),
                  }}
                  onNavigate={() => setOpenSlug(null)}
                />
              )}
            </div>
          )}
        </nav>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <form
            className="container-nq flex items-center gap-3 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (q.trim().length >= 2) {
                window.location.href = `/search?q=${encodeURIComponent(q.trim())}`;
              }
            }}
          >
            <Search className="h-4 w-4 text-[color:var(--nq-gold)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, brands and pages..."
              className="flex-1 bg-transparent py-1.5 text-sm outline-none placeholder:text-[color:var(--nq-stone)]"
              data-testid="header-search-input"
            />
            <button type="submit" className="btn-outline-nq py-1.5" data-testid="header-search-submit">
              Search
            </button>
          </form>
        </div>
      )}

      {/* Mobile drawer, portalled to <body>.

          It cannot live inside <header>: the header uses backdrop-blur, and a
          backdrop-filter makes that element the containing block for any
          position:fixed descendant. The drawer was therefore being clipped to the
          height of the header - all you saw was a thin strip with Call/Enquire. */}
      {mobileOpen && createPortal(
        <div className="fixed inset-0 z-[60] xl:hidden" data-testid="mobile-menu-sheet">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div
            className="scrollbar-thin absolute inset-y-0 left-0 flex w-[86%] max-w-sm flex-col overflow-y-auto border-r"
            style={{ background: "var(--nq-ink)", borderColor: "rgba(198,162,74,0.2)" }}
          >
            <div
              className="sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3"
              style={{ background: "var(--nq-ink)", borderColor: "rgba(255,255,255,0.08)" }}
            >
              <span className="font-bold">
                {logo ? (
                  <img src={logo} alt="NQ Springs" className="h-8 w-auto" />
                ) : (
                  <>NQ<span className="gold-text">SPRINGS</span></>
                )}
              </span>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="rounded-md p-2 hover:bg-white/5"
                data-testid="mobile-menu-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1">
              <MobileMenuTree nav={nav} onNavigate={() => setMobileOpen(false)} />
              <div className="px-2 pb-2">
                <Link
                  to="/site-index"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-[color:var(--nq-stone)] hover:bg-white/5"
                  data-testid="mobile-menu-all-pages"
                >
                  <ChevronRight className="h-4 w-4" /> All pages
                </Link>
              </div>
            </div>

            <div
              className="sticky bottom-0 space-y-2 border-t p-3"
              style={{ background: "var(--nq-ink)", borderColor: "rgba(255,255,255,0.08)" }}
            >
              <Link
                to="/new-vehicle-build"
                onClick={() => setMobileOpen(false)}
                className="btn-outline-nq w-full justify-center"
                data-testid="mobile-menu-new-build"
              >
                New vehicle build
              </Link>
              <div className="grid grid-cols-2 gap-2">
                <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="btn-outline-nq" data-testid="mobile-menu-call">
                  <Phone className="h-4 w-4" /> Call
                </a>
                <Link to="/enquire" onClick={() => setMobileOpen(false)} className="btn-gold" data-testid="mobile-menu-enquire">
                  Enquire
                </Link>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
